import React from 'react'
import { Link } from 'react-router-dom';
import logo from './../assets/logo_2-removebg-preview.png';
import {
  MapPin, Phone, Mail,
  Home, Info, Wrench, Factory, BookOpen, Briefcase, Users, PhoneCall,
  ShoppingCart, Building2, GraduationCap, HeartPulse, Truck, HardHat, UserSearch,
  Shield, FileText, Settings,
} from 'lucide-react';
import { FaInstagram, FaFacebookF, FaLinkedinIn, FaWhatsapp } from 'react-icons/fa';

const scrollTop = () => window.scrollTo(0, 0);

const Footer = () => {
  const whatsappNumber = "+919740026068";
  const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent("Hello! I'd like to know more about your ERP/CRM solutions.")}`;

  const quickLinks = [
    { label: 'Home',         to: '/',             icon: Home },
    { label: 'About Us',     to: '/about',         icon: Info },
    { label: 'Services',     to: '/service',       icon: Wrench },
    { label: 'Industries',   to: '/industries',    icon: Factory },
    { label: 'Case Studies', to: '/case-studies',  icon: Briefcase },
    { label: 'Blogs',        to: '/blogs',         icon: BookOpen },
    { label: 'Careers',      to: '/career',        icon: Users },
    { label: 'Contact Us',   to: '/contact',       icon: PhoneCall },
  ];

  const industries = [
    { label: 'Recruitment & Staffing',    icon: UserSearch },
    { label: 'Manufacturing',             icon: Factory },
    { label: 'Distribution & Logistics',  icon: Truck },
    { label: 'Construction',              icon: HardHat },
    { label: 'Education & LMS',           icon: GraduationCap },
    { label: 'Healthcare',                icon: HeartPulse },
    { label: 'E-Commerce',                icon: ShoppingCart },
    { label: 'Real Estate',               icon: Building2 },
  ];

  const legal = [
    { label: 'Privacy Policy',      to: '/privacypolicy',      icon: Shield },
    { label: 'Terms & Conditions',  to: '/termsandcondition',  icon: FileText },
    { label: 'Maintenance Policy',  to: '/maintenancepolicy',  icon: Settings },
  ];

  return (
    <footer
      className="relative bg-white/10 backdrop-blur-xl border-t border-white/20 pt-14 pb-6 px-[5vw]"
      style={{ boxShadow: '0 -4px 40px rgba(54,48,144,0.08)' }}
    >
      <div className="grid grid-cols-1 gap-10 md:grid-cols-2 lg:grid-cols-4">

        {/* ── Brand ── */}
        <div className="space-y-4">
          <img src={logo} alt="Parnets Software India" className="h-10 w-auto" />
          <p className="text-sm text-[#363090]/70 leading-relaxed">
            Digitizing Businesses. Automating Growth.<br />
            Custom ERP, CRM & Automation Solutions for SMEs since 2012.
          </p>
          <div className="flex gap-3 mt-4">
            <a href="https://www.instagram.com/parnets/" target="_blank" rel="noreferrer"
              className="w-9 h-9 rounded-full bg-[#363090]/10 border border-[#363090]/20 flex items-center justify-center hover:bg-[#d62976] hover:border-[#d62976] hover:text-white text-[#363090] transition-all">
              <FaInstagram size={16} />
            </a>
            <a href="https://www.facebook.com/ParnetsDigitalAdvertisements" target="_blank" rel="noreferrer"
              className="w-9 h-9 rounded-full bg-[#363090]/10 border border-[#363090]/20 flex items-center justify-center hover:bg-[#1877f2] hover:border-[#1877f2] hover:text-white text-[#363090] transition-all">
              <FaFacebookF size={16} />
            </a>
            <a href="https://www.linkedin.com/company/parnets" target="_blank" rel="noreferrer"
              className="w-9 h-9 rounded-full bg-[#363090]/10 border border-[#363090]/20 flex items-center justify-center hover:bg-[#0072b1] hover:border-[#0072b1] hover:text-white text-[#363090] transition-all">
              <FaLinkedinIn size={16} />
            </a>
            <a href={whatsappUrl} target="_blank" rel="noreferrer"
              className="w-9 h-9 rounded-full bg-[#25D366] flex items-center justify-center hover:scale-110 transition-transform text-white">
              <FaWhatsapp size={18} />
            </a>
          </div>
        </div>

        {/* ── Quick Links ── */}
        <div className="space-y-4">
          <h3 className="text-base font-bold text-[#363090] uppercase tracking-wider">Quick Links</h3>
          <ul className="space-y-2">
            {quickLinks.map(({ label, to, icon: Icon }) => (
              <li key={to}>
                <Link
                  to={to}
                  onClick={scrollTop}
                  className="flex items-center gap-2 text-sm text-[#363090]/70 hover:text-[#f04c27] transition-colors group"
                >
                  <Icon size={14} className="text-[#f04c27] shrink-0 group-hover:scale-110 transition-transform" />
                  {label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        {/* ── Industries ── */}
        <div className="space-y-4">
          <h3 className="text-base font-bold text-[#363090] uppercase tracking-wider">Industries We Serve</h3>
          <ul className="space-y-2">
            {industries.map(({ label, icon: Icon }) => (
              <li key={label}>
                <Link
                  to="/industries"
                  onClick={scrollTop}
                  className="flex items-center gap-2 text-sm text-[#363090]/70 hover:text-[#f04c27] transition-colors group"
                >
                  <Icon size={14} className="text-[#f04c27] shrink-0 group-hover:scale-110 transition-transform" />
                  {label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        {/* ── Contact ── */}
        <div className="space-y-4">
          <h3 className="text-base font-bold text-[#363090] uppercase tracking-wider">Contact Us</h3>
          <ul className="space-y-3 text-sm text-[#363090]/70">
            <li className="flex items-start gap-2">
              <MapPin size={15} className="text-[#f04c27] mt-0.5 shrink-0" />
              <span>104/1 Singapura Main Road, near Reliance Smart, Bengaluru, Karnataka – 560097</span>
            </li>
            <li className="flex items-center gap-2">
              <Phone size={15} className="text-[#f04c27] shrink-0" />
              <a href="tel:+919740026068" className="hover:text-[#f04c27] transition-colors">+91 97400 26068</a>
            </li>
            <li className="flex items-center gap-2">
              <Mail size={15} className="text-[#f04c27] shrink-0" />
              <a href="mailto:hello@parnetsgroup.com" className="hover:text-[#f04c27] transition-colors">hello@parnetsgroup.com</a>
            </li>
          </ul>

          {/* Legal */}
          <div className="pt-2 space-y-1.5">
            {legal.map(({ label, to, icon: Icon }) => (
              <Link
                key={to}
                to={to}
                onClick={scrollTop}
                className="flex items-center gap-2 text-xs text-[#363090]/50 hover:text-[#f04c27] transition-colors group"
              >
                <Icon size={12} className="text-[#363090]/40 shrink-0 group-hover:text-[#f04c27] transition-colors" />
                {label}
              </Link>
            ))}
          </div>
        </div>
      </div>

      {/* ── Bottom bar ── */}
      <div className="mt-10 pt-6 border-t border-[#363090]/15 flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-[#363090]/50">
        <p>
          © {new Date().getFullYear()}{' '}
          <span className="text-[#363090] font-semibold">Par</span><span className="text-[#f04c27] font-semibold">Nets</span>{' '}
          Software India PVT LTD. All rights reserved.
        </p>
        <p>Digitizing Businesses. Automating Growth.</p>
      </div>
    </footer>
  );
};

export default Footer;
